import { useRef, useState, type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent } from 'react';

import {
  GRADIENT_PRESETS,
  MAX_GRADIENT_KEYS,
  addGradientKey,
  clamp01,
  cleanGradient,
  cssColor,
  gradientCss,
  r3,
  removeGradientKey,
  reverseGradient,
  rgba,
  setGradientKey,
  type Gradient,
  type GradientKeyKind,
} from '../../lib/curves.js';
import { ColorField, IconButton, MenuButton, NumberField, type MenuItem } from './controls.js';
import { Icon } from './icons.js';
import './curves.css';

/** Checkerboard under the gradient so alpha shows. */
export const CHECKER = 'repeating-conic-gradient(#8a8a8a 0% 25%, #c8c8c8 0% 50%)';

type AnyKey = number[];
const ch = (key: AnyKey, i: number) => key[i] ?? 0;

/**
 * Reusable gradient editor (Unity's Gradient Editor in miniature): alpha
 * keys above the bar, color keys below it (at most eight of each). Drag a
 * marker to move it, double-click a track or the bar to add a key, Delete or
 * right-click removes the selected one; the selected key's color / alpha and
 * time are edited below. Blend / fixed mode, presets and reverse are in the
 * row. Gradients are `{ colorKeys, alphaKeys, mode }` (lib/curves.ts).
 * `onGestureStart` / `onGestureEnd` bracket marker drags (one undo step).
 */
export function GradientEditor({
  value,
  onChange,
  playhead = null,
  onGestureStart,
  onGestureEnd,
}: {
  value: Gradient | undefined;
  onChange: (g: Gradient) => void;
  /** Normalized time marked on the bar. */
  playhead?: number | null;
  onGestureStart?: () => void;
  onGestureEnd?: () => void;
}) {
  const bar = useRef<HTMLDivElement>(null);
  const drag = useRef<{ kind: GradientKeyKind; index: number; g: Gradient; pointer: number } | null>(null);
  const [sel, setSel] = useState<{ kind: GradientKeyKind; index: number } | null>(null);
  const g: Gradient = value ?? {};
  const colorKeys = g.colorKeys ?? [];
  const alphaKeys = g.alphaKeys ?? [];
  const keysOf = (kind: GradientKeyKind): AnyKey[] => (kind === 'color' ? colorKeys : alphaKeys);

  const timeAt = (clientX: number) => {
    const rect = bar.current!.getBoundingClientRect();
    return r3(clamp01((clientX - rect.left) / Math.max(1, rect.width)));
  };

  function setKey(kind: GradientKeyKind, index: number, key: AnyKey, base = g) {
    const res = setGradientKey(base, kind, index, key);
    if (res.index < 0) return null;
    onChange(res.gradient);
    setSel({ kind, index: res.index });
    return res;
  }

  function addKey(kind: GradientKeyKind, e: ReactMouseEvent) {
    const res = addGradientKey(g, kind, timeAt(e.clientX));
    if (res.index < 0) return;
    onChange(res.gradient);
    setSel({ kind, index: res.index });
  }

  function remove(kind: GradientKeyKind, index: number) {
    onChange(removeGradientKey(g, kind, index));
    setSel(null);
  }

  function startDrag(e: ReactPointerEvent<HTMLButtonElement>, kind: GradientKeyKind, index: number) {
    e.stopPropagation();
    if (e.button !== 0) return;
    e.currentTarget.setPointerCapture(e.pointerId);
    setSel({ kind, index });
    drag.current = { kind, index, g, pointer: e.pointerId };
    onGestureStart?.();
  }

  function moveDrag(e: ReactPointerEvent<HTMLButtonElement>) {
    const d = drag.current;
    if (!d || d.pointer !== e.pointerId) return;
    const old = (d.kind === 'color' ? d.g.colorKeys : d.g.alphaKeys)?.[d.index];
    if (!old) return;
    const t = timeAt(e.clientX);
    if (t === ch(old, 0)) return;
    const res = setKey(d.kind, d.index, [t, ...old.slice(1)], d.g);
    if (res) {
      d.g = res.gradient;
      d.index = res.index;
    }
  }

  const endDrag = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (drag.current?.pointer !== e.pointerId) return;
    drag.current = null;
    onGestureEnd?.();
  };

  const marker = (kind: GradientKeyKind, key: AnyKey, index: number) => {
    const isSel = sel?.kind === kind && sel.index === index;
    const [t, c1, c2, c3] = [ch(key, 0), ch(key, 1), ch(key, 2), ch(key, 3)];
    const fill = kind === 'color' ? cssColor([c1, c2, c3, 1]) : cssColor([c1, c1, c1, 1]);
    return (
      <button
        key={index}
        type="button"
        className={`gd-marker${isSel ? ' is-selected' : ''}`}
        style={{ left: `${clamp01(t) * 100}%`, background: fill }}
        title={`${kind === 'color' ? 'Color key' : `Alpha ${r3(c1)}`} at ${r3(t)} (right-click to remove)`}
        onPointerDown={(e) => startDrag(e, kind, index)}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerCancel={endDrag}
        onDoubleClick={(e) => e.stopPropagation()}
        onContextMenu={(e) => {
          e.preventDefault();
          remove(kind, index);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Delete' || e.key === 'Backspace') {
            e.preventDefault();
            e.stopPropagation();
            remove(kind, index);
          }
        }}
      />
    );
  };

  const selKey = sel ? keysOf(sel.kind)[sel.index] : undefined;
  const presets: MenuItem[] = [
    ...GRADIENT_PRESETS.map((p) => ({
      id: p.name,
      label: p.name,
      run: () => {
        onChange(cleanGradient({ ...p.gradient, mode: g.mode }));
        setSel(null);
      },
    })),
  ];
  const full = (kind: GradientKeyKind) => keysOf(kind).length >= MAX_GRADIENT_KEYS;

  return (
    <div className="gd-editor">
      <div
        className="gd-track is-alpha"
        title={full('alpha') ? 'At most eight alpha keys' : 'Alpha keys (double-click to add)'}
        onDoubleClick={(e) => addKey('alpha', e)}
      >
        {alphaKeys.map((k, i) => marker('alpha', k, i))}
      </div>
      <div
        ref={bar}
        className="gd-bar"
        style={{
          backgroundImage: `${gradientCss(g)}, ${CHECKER}`,
          backgroundSize: '100% 100%, 10px 10px',
          backgroundRepeat: 'no-repeat, repeat',
        }}
        title="Double-click to add a color key"
        onDoubleClick={(e) => addKey('color', e)}
      >
        {playhead !== null && playhead >= 0 && playhead <= 1 && <span className="gd-playhead" style={{ left: `${playhead * 100}%` }} />}
      </div>
      <div
        className="gd-track is-color"
        title={full('color') ? 'At most eight color keys' : 'Color keys (double-click to add)'}
        onDoubleClick={(e) => addKey('color', e)}
      >
        {colorKeys.map((k, i) => marker('color', k, i))}
      </div>
      <div className="gd-row">
        {sel && selKey ? (
          <>
            {sel.kind === 'color' ? (
              <ColorField
                value={[ch(selKey, 1), ch(selKey, 2), ch(selKey, 3)]}
                alpha={false}
                onChange={(c) => setKey('color', sel.index, [ch(selKey, 0), ...rgba(c).slice(0, 3)])}
              />
            ) : (
              <NumberField
                label="A"
                title="Alpha"
                value={ch(selKey, 1)}
                step={0.05}
                min={0}
                max={1}
                onChange={(a) => setKey('alpha', sel.index, [ch(selKey, 0), a])}
              />
            )}
            <NumberField
              label="T"
              title="Key time (0..1)"
              value={ch(selKey, 0)}
              step={0.01}
              min={0}
              max={1}
              onChange={(t) => setKey(sel.kind, sel.index, [t, ...selKey.slice(1)])}
            />
            <IconButton
              icon="trash"
              title="Delete key"
              size={13}
              disabled={sel.kind === 'color' && colorKeys.length <= 1}
              onClick={() => remove(sel.kind, sel.index)}
            />
          </>
        ) : (
          <span className="cv-hint">double-click adds a key (alpha above, color below)</span>
        )}
        <div className="cv-seg" role="group" aria-label="Gradient mode" title="Blend: interpolate between keys. Fixed: hold each key up to its time.">
          {(['blend', 'fixed'] as const).map((m) => (
            <button
              key={m}
              type="button"
              className={(g.mode ?? 'blend') === m ? 'is-on' : ''}
              onClick={() => onChange(cleanGradient({ ...g, mode: m }))}
            >
              {m === 'blend' ? 'Blend' : 'Fixed'}
            </button>
          ))}
        </div>
        <IconButton
          icon="mirror"
          title="Reverse"
          size={13}
          onClick={() => {
            onChange(reverseGradient(g));
            setSel(null);
          }}
        />
        <MenuButton items={presets} className="ibtn" title="Gradient presets">
          <Icon name="list" size={13} />
        </MenuButton>
      </div>
    </div>
  );
}
